import React from "react";
import { Card, CardActionArea, CardContent, CardMedia, Chip, IconButton, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { getPropertyStatusSx } from "./propertyStatusStyles";

const PLACEHOLDER_IMAGE = "/images/property-placeholder.svg";

export default function PropertyCard({ property, onClick, onPreview }) {
  const propertyName = property.name || "Unnamed property";
  const location = [property.city, property.state].filter(Boolean).join(", ");
  const locality = [location, property.zipCode].filter(Boolean).join(" ");

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        position: "relative",
        borderRadius: 2,
        borderColor: "#dfe5ec",
        boxShadow: "0 4px 14px rgba(15,23,42,.045)",
        overflow: "hidden",
        transition: "transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease",
        "&:hover, &:focus-within": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 24px rgba(0,51,108,.12)",
          borderColor: "rgba(0,51,108,.28)",
        },
      }}
    >
      <IconButton
        aria-label={`Preview ${propertyName} profile`}
        onClick={(event) => {
          event.stopPropagation();
          onPreview?.(property);
        }}
        sx={{ position: "absolute", top: 8, right: 8, zIndex: 2, color: BRAND_BLUE, bgcolor: "rgba(255,255,255,.92)", boxShadow: "0 2px 8px rgba(0,0,0,.16)", "&:hover": { bgcolor: "white" } }}
      >
        <MoreVertIcon />
      </IconButton>
      <CardActionArea
        onClick={onClick}
        aria-label={`View ${propertyName}`}
        sx={{
          alignItems: "stretch",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          "&:focus-visible": { outline: "3px solid rgba(99,167,10,.55)", outlineOffset: -3 },
        }}
      >
        <CardMedia
          component="img"
          image={property.image || PLACEHOLDER_IMAGE}
          alt={property.image ? propertyName : "Property image unavailable"}
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = PLACEHOLDER_IMAGE;
          }}
          sx={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover" }}
        />

        <CardContent sx={{ display: "flex", flex: 1, flexDirection: "column", width: "100%", p: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ color: BRAND_BLUE, fontWeight: 800, lineHeight: 1.3 }}>
            {propertyName}
          </Typography>
          {property.streetAddress && <Typography variant="body2" sx={{ color: BRAND_BLUE }}>{property.streetAddress}</Typography>}
          {locality && (
            <Typography variant="body2" sx={{ color: BRAND_BLUE }}>
              {locality}
            </Typography>
          )}
          <Typography variant="body1" sx={{ mt: "auto", pt: 2, color: BRAND_GREEN, fontWeight: 800 }}>{Number(property.squareFeet || 0).toLocaleString()} sq. ft.</Typography>
          <Chip label={property.status || "Occupied"} size="small" variant="outlined" sx={{ mt: 1.25, alignSelf: "flex-start", height: 24, fontWeight: 800, ...getPropertyStatusSx(property.status) }} />
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
